// Composer image attachments → the wire shape the assistant transport sends.
//
// TWO composer shapes exist today and both land here:
//   • the drawer/sidebar UploadBtn (shared/components/_simone/Chat.js) pushes a
//     BARE data-URL string — `reader.result` straight from FileReader;
//   • the canvas composer (canvas/components/CanvasPromptTextarea.js) pushes
//     `{ key, name, mime, size, dataUrl }` from its drop/paste/upload handlers.
// `normalizeComposerImages` accepts either and emits ONE shape.
//
// Bare shared-library import, deferred-call style (circular-init safety).
import _wsShared from '@symbo-ls/workspace-shared'

// The shipped English, for a caller that has no element to speak through
// (the unit tests, a headless send) — never a raw key.
const _shippedEnglish = (key) => String(_wsShared.functions.i18nStaticEnglish()[key] || key)

// The caps below MIRROR the server's authority — agent-core
// `IMAGE_ATTACHMENT_LIMITS` (server/packages/agent-core/messages/
// messageContent.js), which derives them from Mongo's 16 MB BSON document
// limit and the API's `config.bodyLimits.maxJsonMb` body limit. The server
// enforces; this copy exists only so the user is told what will be dropped
// BEFORE a multi-megabyte upload travels the wire. If the two ever disagree
// the server wins and the extra images are simply refused there.
export const IMAGE_ATTACHMENT_LIMITS = {
  maxCount: 4,
  maxBytesPerImage: 3 * 1024 * 1024,
  maxBytesTotal: 6 * 1024 * 1024,
  // The four formats the Anthropic image block and the OpenAI `image_url`
  // data-URL block both accept.
  mimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
}

// HEADER ONLY — deliberately not `([\s\S]*)$`. This runs on the reactive
// awareness path (pageContext.js) where a capture group would copy the whole
// multi-megabyte base64 body on every render. Everything downstream needs the
// data URL whole, so it is never split.
const DATA_URL_HEAD_RE = /^data:([a-z0-9.+/-]+);base64,/i

// Decoded byte length of a base64 data URL, from its LENGTH and its last two
// characters. No decode, no slice — an oversize image costs nothing to weigh.
export const dataUrlByteLength = (dataUrl, headerLength) => {
  const s = String(dataUrl || '')
  const encoded = Math.max(0, s.length - (headerLength || 0))
  if (!encoded) return 0
  const padding = s.endsWith('==') ? 2 : s.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((encoded * 3) / 4) - padding)
}

export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * @param {Array<string|object>} images — composer state.images, either shape.
 * @returns {{
 *   attachments: Array<{name,mime,size,dataUrl}>,
 *   rejected: Array<{name,reason}>,
 *   decisions: Array<{index,accepted,reason}>
 * }}
 *   `attachments` is [] for an empty/absent input, which is what keeps a
 *   text-only send byte-identical to its pre-attachment envelope (the caller
 *   omits the key entirely on an empty array). `decisions` is parallel to the
 *   INPUT array so a renderer can label each chip it already draws.
 */
export const normalizeComposerImages = (images) => {
  const attachments = []
  const rejected = []
  const decisions = []
  let total = 0

  const list = Array.isArray(images) ? images : []
  for (let index = 0; index < list.length; index++) {
    const entry = list[index]
    const refuse = (name, reason) => {
      rejected.push({ name, reason })
      decisions.push({ index, accepted: false, reason })
    }
    if (!entry) {
      refuse('image', 'empty')
      continue
    }
    const raw = typeof entry === 'string' ? { dataUrl: entry } : entry
    const dataUrl = String(raw.dataUrl || raw.url || '')
    const name = String(raw.name || `image-${index + 1}`).slice(0, 200)
    const head = DATA_URL_HEAD_RE.exec(dataUrl)
    if (!head) {
      refuse(name, 'not_a_base64_data_url')
      continue
    }
    const mime = String(raw.mime || raw.type || head[1] || '').toLowerCase()
    if (!IMAGE_ATTACHMENT_LIMITS.mimeTypes.includes(mime)) {
      refuse(name, `unsupported_type:${mime || 'unknown'}`)
      continue
    }
    if (attachments.length >= IMAGE_ATTACHMENT_LIMITS.maxCount) {
      refuse(name, 'too_many')
      continue
    }
    // Trust the composer's own byte count when it has one (the File it read),
    // otherwise weigh the data URL — the drawer UploadBtn keeps no size.
    const size =
      Number(raw.size) > 0 ? Number(raw.size) : dataUrlByteLength(dataUrl, head[0].length)
    if (size > IMAGE_ATTACHMENT_LIMITS.maxBytesPerImage) {
      refuse(name, 'too_large')
      continue
    }
    if (total + size > IMAGE_ATTACHMENT_LIMITS.maxBytesTotal) {
      refuse(name, 'total_too_large')
      continue
    }
    total += size
    attachments.push({ name, mime, size, dataUrl })
    decisions.push({ index, accepted: true, reason: null })
  }

  return { attachments, rejected, decisions }
}

// One human sentence naming every refused image and why. Null when nothing was
// refused — the caller only shows a note when there is something to say.
// `say(key)` resolves a bundle key; sendPrompt passes its element's polyglot, so
// the note reads in the session language. Every reason is a whole bundle
// sentence with named placeholders, never a frame with a word slotted in.
export const describeRejectedImages = (rejected, say = _shippedEnglish) => {
  if (!Array.isArray(rejected) || !rejected.length) return null
  const why = {
    too_many: () =>
      say('workspace.simone.images.tooMany').replace('{max}', () =>
        String(IMAGE_ATTACHMENT_LIMITS.maxCount)
      ),
    too_large: () =>
      say('workspace.simone.images.tooLarge').replace('{size}', () =>
        formatBytes(IMAGE_ATTACHMENT_LIMITS.maxBytesPerImage)
      ),
    total_too_large: () =>
      say('workspace.simone.images.totalTooLarge').replace('{size}', () =>
        formatBytes(IMAGE_ATTACHMENT_LIMITS.maxBytesTotal)
      ),
    not_a_base64_data_url: () => say('workspace.simone.images.notReadable'),
    empty: () => say('workspace.simone.images.empty')
  }
  const lines = rejected.map((r) => {
    const reason = String(r?.reason || '')
    const label = reason.startsWith('unsupported_type:')
      ? say('workspace.simone.images.unsupported').replace('{type}', () =>
          reason.slice('unsupported_type:'.length)
        )
      : why[reason]
        ? why[reason]()
        : reason || say('workspace.simone.images.notAccepted')
    return `${r?.name || say('workspace.simone.images.defaultName')} — ${label}`
  })
  return say('workspace.simone.images.notSent').replace('{list}', () => lines.join('; '))
}
